<template>
  <div class="search-page">
    <header class="page-header">
      <div>
        <span class="page-kicker">翻书 · 全局搜索</span>
        <h1>搜索</h1>
        <p>按书名或作者查找可访问的电子书。</p>
      </div>
    </header>

    <form class="search-form" role="search" @submit.prevent="handleSearch">
      <label class="search-box" for="global-search-input">
        <nricon name="search" :size="20" />
        <input id="global-search-input" v-model="searchText" type="search" autocomplete="off" placeholder="搜索书名或作者" @input="scheduleSearch">
        <button v-if="searchText" class="clear-input" type="button" aria-label="清除搜索词" @click="clearSearch"><nricon name="close" /></button>
      </label>
      <button class="btn btn--primary" type="submit">搜索</button>
    </form>

    <section class="filters" aria-label="搜索筛选">
      <div class="scope-tabs" role="group" aria-label="搜索范围">
        <button v-for="option in scopes" :key="option.value" type="button" :class="{ active: scope === option.value }" :aria-pressed="scope === option.value" @click="setScope(option.value)">{{ option.label }}</button>
      </div>
      <div class="categories media-types" role="group" aria-label="媒介类型">
        <button v-for="option in mediaOptions" :key="option.value" type="button" class="category-chip" :class="{ active: mediaType === option.value }" :aria-pressed="mediaType === option.value" @click="setMediaType(option.value)">{{ option.label }}</button>
      </div>
      <div class="categories" role="group" aria-label="分类">
        <button type="button" class="category-chip" :class="{ active: !selectedCategory }" :aria-pressed="!selectedCategory" @click="setCategory('')">全部分类</button>
        <button v-for="category in categories" :key="category" type="button" class="category-chip" :class="{ active: selectedCategory === category }" :aria-pressed="selectedCategory === category" @click="setCategory(selectedCategory === category ? '' : category)">{{ category }}</button>
      </div>
    </section>

    <div class="search-content">
      <div v-if="loading" class="loading-state" role="status"><nricon name="loading" :size="21" /> 正在搜索</div>
      <div v-else-if="error" class="state-panel error-state">
        <nricon name="warning" :size="22" /><div><strong>搜索失败</strong><p>{{ errorMessage }}</p></div>
        <button class="btn btn--sm" type="button" @click="loadResults">重试</button>
      </div>
      <section v-else-if="!searchText.trim()" class="search-history" aria-label="搜索历史">
        <div class="section-title"><h2>搜索历史</h2><button v-if="searchHistory.length" class="text-button" type="button" @click="clearHistory">清空</button></div>
        <div v-if="searchHistory.length" class="history-tags">
          <span v-for="item in searchHistory" :key="item" class="history-chip">
            <button type="button" @click="searchText = item; handleSearch()">{{ item }}</button>
            <button type="button" :aria-label="`删除搜索历史 ${item}`" @click="removeHistory(item)"><nricon name="close" :size="13" /></button>
          </span>
        </div>
        <div v-else class="history-empty">输入书名或作者，开始搜索。</div>
      </section>
      <section v-else-if="searchResults.length" class="search-results" aria-live="polite">
        <div class="result-count">找到 {{ total }} 个结果</div>
        <div class="result-list">
          <button v-for="book in searchResults" :key="book.id" class="result-item" type="button" @click="goToDetail(book.id)">
            <span class="result-cover" :class="coverClass(book)">
              <protected-cover v-if="book.coverImage && !brokenCovers[book.id]" :src="book.coverImage" :alt="''" @error="markCoverBroken(book.id)" />
              <span v-else>{{ coverInitial(book) }}</span>
            </span>
            <span class="result-info">
              <strong>{{ cleanTitle(book.title) }}</strong>
              <span class="result-author">{{ book.author || '佚名' }}</span>
              <span v-if="book.description" class="result-desc">{{ book.description }}</span>
              <span class="result-meta"><span>{{ mediaLabel(book) }}</span><span v-if="book.category">{{ book.category }}</span><span v-if="book.visibility === 'PUBLIC'">公开</span><span v-else-if="isOwner(book)">我的上传</span></span>
            </span>
            <nricon class="result-arrow" name="arrow-right" :size="18" />
          </button>
        </div>
        <nav v-if="totalPages > 1" class="pager" aria-label="搜索结果分页">
          <button type="button" :disabled="page <= 1" aria-label="上一页" @click="changePage(page - 1)"><nricon name="arrow-left" /></button>
          <span>第 {{ page }} / {{ totalPages }} 页</span>
          <button type="button" :disabled="page >= totalPages" aria-label="下一页" @click="changePage(page + 1)"><nricon name="arrow-right" /></button>
        </nav>
      </section>
      <div v-else class="no-result" aria-live="polite">
        <span class="empty-mark"><nricon name="search" :size="24" /></span>
        <div><strong>没有找到相关内容</strong><p>没有找到「{{ searchText }}」，可以清除分类或修改关键词。</p></div>
        <button class="btn btn--sm" type="button" @click="clearFilters">清除筛选</button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import ProtectedCover from '@/components/common/ProtectedCover.vue'
import { CATEGORIES } from '@/utils/categories'
import { cleanTitle } from '@/utils/title'

const SCOPE_VALUES = ['all', 'mine', 'public']

export default {
  name: 'SearchPage',
  components: { ProtectedCover },
  data() {
    return {
      searchText: '', selectedCategory: '', mediaType: '', scope: 'all', categories: CATEGORIES,
      mediaOptions: [{ value: '', label: '全部媒介' }, { value: 'TEXT', label: '电子书' }, { value: 'PDF', label: 'PDF' }, { value: 'AUDIO', label: '有声书' }],
      scopes: [{ value: 'all', label: '全部可访问' }, { value: 'mine', label: '我的书库' }, { value: 'public', label: '公共书库' }],
      searchResults: [], page: 1, pageSize: 20, total: 0, loading: false, error: false, errorMessage: '',
      searchTimer: null, searchHistory: JSON.parse(localStorage.getItem('searchHistory') || '[]'), brokenCovers: {}
    }
  },
  computed: { totalPages() { return Math.ceil(this.total / this.pageSize) } },
  watch: {
    '$route.query': {
      handler() {
        this.readQuery()
        if (this.searchText.trim()) this.loadResults()
        else { this.searchResults = []; this.total = 0 }
      },
      deep: true
    }
  },
  methods: {
    cleanTitle,
    readQuery() {
      const q = this.$route.query || {}
      this.searchText = q.keyword || ''
      this.selectedCategory = q.category || ''
      this.mediaType = ['TEXT', 'PDF', 'AUDIO'].includes(q.mediaType) ? q.mediaType : ''
      this.scope = SCOPE_VALUES.includes(q.scope) ? q.scope : 'all'
      this.page = Math.max(1, parseInt(q.page, 10) || 1)
      if (!this.$store.state.token && this.scope === 'mine') this.scope = 'public'
    },
    writeQuery() {
      const query = {}
      if (this.searchText.trim()) query.keyword = this.searchText.trim()
      if (this.selectedCategory) query.category = this.selectedCategory
      if (this.mediaType) query.mediaType = this.mediaType
      if (this.scope !== 'all') query.scope = this.scope
      if (this.page > 1) query.page = String(this.page)
      if (JSON.stringify(query) === JSON.stringify(this.$route.query || {})) {
        if (this.searchText.trim()) this.loadResults()
        return
      }
      this.$router.replace({ name: 'Search', query }).catch(() => {})
    },
    async handleSearch() {
      if (this.searchTimer) { clearTimeout(this.searchTimer); this.searchTimer = null }
      this.page = 1
      if (!this.searchText.trim()) { this.searchResults = []; this.total = 0; this.writeQuery(); return }
      this.addToHistory(this.searchText.trim())
      this.writeQuery()
    },
    scheduleSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.handleSearch(), 350)
    },
    async loadResults() {
      if (!this.searchText.trim()) { this.searchResults = []; this.total = 0; return }
      this.loading = true; this.error = false
      try {
        const params = { keyword: this.searchText.trim(), category: this.selectedCategory || undefined, mediaType: this.mediaType || undefined, page: this.page, pageSize: this.pageSize }
        const backendScope = this.scope === 'mine' ? 'library' : this.scope
        const result = this.scope === 'all'
          ? await api.book.searchBooks(params)
          : await api.book.getBooks({ ...params, scope: backendScope })
        const { data } = result
        this.searchResults = data.list || []
        this.total = Number(data.total) || 0
      } catch (error) {
        this.error = true
        this.errorMessage = (error.response && error.response.data && error.response.data.message) || '请检查网络后重试。'
        this.searchResults = []; this.total = 0
      } finally { this.loading = false }
    },
    setScope(value) { this.scope = value; this.page = 1; this.writeQuery() },
    setCategory(value) { this.selectedCategory = value; this.page = 1; this.writeQuery() },
    setMediaType(value) { this.mediaType = value; this.page = 1; this.writeQuery() },
    clearSearch() { this.searchText = ''; this.page = 1; this.searchResults = []; this.total = 0; this.writeQuery() },
    clearFilters() { this.selectedCategory = ''; this.mediaType = ''; this.page = 1; this.writeQuery() },
    changePage(page) { if (page < 1 || page > this.totalPages) return; this.page = page; this.writeQuery(); window.scrollTo({ top: 0, behavior: 'smooth' }) },
    addToHistory(keyword) {
      const history = [keyword, ...this.searchHistory.filter(item => item !== keyword)].slice(0, 10)
      this.searchHistory = history
      localStorage.setItem('searchHistory', JSON.stringify(history))
    },
    removeHistory(keyword) { this.searchHistory = this.searchHistory.filter(item => item !== keyword); localStorage.setItem('searchHistory', JSON.stringify(this.searchHistory)) },
    clearHistory() { this.searchHistory = []; localStorage.setItem('searchHistory', '[]') },
    coverIndex(book) { return Math.abs(Array.from(cleanTitle(book.title) || String(book.id)).reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) | 0, 0)) % 4 },
    coverClass(book) { return `cover-${this.coverIndex(book)}` },
    coverInitial(book) { return (cleanTitle(book.title) || '书').slice(0, 1) },
    mediaLabel(book) { return ({ PDF: 'PDF', AUDIO: '有声书' })[book.mediaType] || '电子书' },
    markCoverBroken(id) { this.$set(this.brokenCovers, id, true) },
    isOwner(book) { return !!(this.$store.state.user && book.userId === this.$store.state.user.id) },
    goToDetail(id) { this.$router.push({ name: 'BookDetail', params: { id: String(id) } }) }
  },
  created() { this.readQuery(); if (this.searchText.trim()) this.loadResults() },
  beforeDestroy() { if (this.searchTimer) clearTimeout(this.searchTimer) }
}
</script>

<style lang="scss" scoped>
.search-page { max-width: 980px; margin: 0 auto; padding: 38px 0 45px; }
.page-header { margin-bottom: 22px; }
.page-kicker { color: var(--nr-accent); font-size: 12px; font-weight: 700; letter-spacing: .07em; }
h1 { margin: 5px 0 4px; color: var(--nr-text); font-size: 30px; font-weight: 650; }
.page-header p { margin: 0; color: var(--nr-text-2); font-size: 14px; }
.search-form { display: flex; gap: 10px; }
.search-box { display: flex; min-width: 0; min-height: 50px; flex: 1; align-items: center; gap: 11px; padding: 0 14px; color: var(--nr-text-3); border: 1px solid var(--nr-border); border-radius: 11px; background: var(--nr-surface); transition: border-color 160ms ease, box-shadow 160ms ease; &:focus-within { border-color: var(--nr-accent); box-shadow: var(--nr-ring); }
  input { width: 100%; min-width: 0; color: var(--nr-text); border: 0; outline: 0; background: transparent; font-size: 15px; &::placeholder { color: var(--nr-text-3); } }
}
.clear-input { display: grid; width: 32px; height: 32px; flex: 0 0 32px; place-items: center; color: var(--nr-text-3); border: 0; border-radius: 50%; background: transparent; cursor: pointer; &:hover { background: var(--nr-surface-2); } }
.filters { display: grid; gap: 12px; margin: 18px 0 24px; }
.scope-tabs, .categories { display: flex; flex-wrap: wrap; gap: 7px; }
.scope-tabs button, .category-chip { min-height: 35px; padding: 0 12px; color: var(--nr-text-2); border: 1px solid var(--nr-border); border-radius: 8px; background: var(--nr-surface); font-size: 12px; cursor: pointer; transition: border-color 160ms ease, color 160ms ease, background 160ms ease; &:hover { border-color: var(--nr-accent); } &.active { color: var(--nr-accent-text); border-color: var(--nr-accent); background: var(--nr-accent-bg); font-weight: 650; } }
.categories { gap: 6px; }
.category-chip { min-height: 31px; border-color: transparent; background: transparent; }
.search-content { min-height: 220px; }
.loading-state { display: flex; min-height: 160px; align-items: center; justify-content: center; gap: 10px; color: var(--nr-text-2); font-size: 13px; }
.section-title { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.section-title h2 { margin: 0; color: var(--nr-text); font-size: 17px; }
.text-button { color: var(--nr-text-3); border: 0; background: transparent; font-size: 12px; cursor: pointer; }
.history-empty { padding: 23px 0; color: var(--nr-text-3); font-size: 13px; }
.history-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.history-chip { display: inline-flex; min-height: 34px; align-items: center; overflow: hidden; border: 1px solid var(--nr-border); border-radius: 99px; background: var(--nr-surface); }
.history-chip button { min-height: 32px; padding: 0 10px; color: var(--nr-text-2); border: 0; background: transparent; font-size: 12px; cursor: pointer; &:hover { color: var(--nr-accent-text); } }
.history-chip button + button { display: grid; width: 31px; padding: 0; place-items: center; border-left: 1px solid var(--nr-border); }
.result-count { margin: 0 0 10px; color: var(--nr-text-3); font-size: 12px; }
.result-list { display: grid; gap: 9px; }
.result-item { display: flex; width: 100%; min-width: 0; align-items: center; gap: 15px; padding: 12px; color: inherit; border: 1px solid var(--nr-border); border-radius: 12px; background: var(--nr-surface); text-align: left; cursor: pointer; transition: background 160ms ease, border-color 160ms ease; &:hover { border-color: color-mix(in srgb, var(--nr-accent) 45%, var(--nr-border)); background: color-mix(in srgb, var(--nr-surface) 84%, var(--nr-accent-bg)); } }
.result-cover { position: relative; display: grid; width: 54px; height: 76px; flex: 0 0 54px; overflow: hidden; place-items: center; color: #fff; border-radius: 4px; box-shadow: var(--nr-shadow); font-family: var(--nr-font-display); font-size: 24px; img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; } }
.cover-0 { background: linear-gradient(145deg,#557462,#293d34); } .cover-1 { background: linear-gradient(145deg,#65798a,#36434c); } .cover-2 { background: linear-gradient(145deg,#a17456,#624536); } .cover-3 { background: linear-gradient(145deg,#766d86,#454153); }
.result-info { display: grid; min-width: 0; flex: 1; gap: 3px; }
.result-info strong { overflow: hidden; color: var(--nr-text); font-size: 15px; text-overflow: ellipsis; white-space: nowrap; }
.result-author { color: var(--nr-text-2); font-size: 12px; }
.result-desc { display: -webkit-box; overflow: hidden; color: var(--nr-text-3); font-size: 12px; line-height: 1.5; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.result-meta { display: flex; gap: 10px; margin-top: 2px; color: var(--nr-text-3); font-size: 10px; }
.result-arrow { color: var(--nr-text-3); }
.pager { display: flex; align-items: center; justify-content: center; gap: 14px; margin-top: 24px; color: var(--nr-text-3); font-size: 12px; }
.pager button { display: grid; width: 38px; height: 38px; place-items: center; color: var(--nr-text-2); border: 1px solid var(--nr-border); border-radius: 8px; background: var(--nr-surface); cursor: pointer; &:disabled { opacity: .45; cursor: default; } }
.state-panel, .no-result { display: flex; min-height: 120px; align-items: center; gap: 14px; padding: 20px; color: var(--nr-text-2); border: 1px solid var(--nr-border); border-radius: 14px; background: var(--nr-surface); }
.state-panel strong, .no-result strong { display: block; color: var(--nr-text); font-size: 14px; }
.state-panel p, .no-result p { margin: 4px 0 0; font-size: 12px; }
.state-panel .btn, .no-result .btn { margin-left: auto; }
.empty-mark { display: grid; width: 46px; height: 46px; flex: 0 0 46px; place-items: center; color: var(--nr-accent); border-radius: 13px; background: var(--nr-accent-bg); }
@media (max-width: 767px) {
  .search-page { padding: 27px 0 20px; }
  h1 { font-size: 25px; }
  .page-header p { font-size: 12px; }
  .search-form { gap: 7px; }
  .search-form > .btn { padding-inline: 13px; }
  .search-box { min-height: 46px; padding-inline: 11px; }
  .result-item { gap: 11px; padding: 10px; }
  .result-desc { -webkit-line-clamp: 1; }
}
</style>
